import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  pageCard: {
    borderRadius: 24,
    padding: theme.spacing(3),
    boxShadow: "0 18px 40px rgba(16, 63, 104, 0.08)"
  },
  infoBox: {
    borderRadius: 18,
    padding: theme.spacing(2),
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.04)"
        : "rgba(31,84,132,0.05)"
  },
  managerCard: {
    borderRadius: 20,
    padding: theme.spacing(2.25),
    height: "100%",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.06)"
      : "1px solid rgba(28,78,125,0.08)",
    background: theme.palette.type === "dark"
      ? "rgba(255,255,255,0.025)"
      : "rgba(255,255,255,0.95)"
  },
  chipWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    minHeight: 88,
    alignContent: "flex-start"
  },
  emptyState: {
    padding: theme.spacing(1.5),
    borderRadius: 12,
    width: "100%",
    border: theme.palette.type === "dark"
      ? "1px dashed rgba(255,255,255,0.12)"
      : "1px dashed rgba(28,78,125,0.18)"
  },
  footerActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2.5)
  }
}));

const parseCatalogText = value =>
  String(value || "")
    .split(/\r?\n/)
    .map(item => item.trim())
    .filter(Boolean);

const normalizeCatalogValue = value => String(value || "").trim().replace(/\s+/g, " ");

const ClientCatalogs = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.super || user?.profile === "admin";

  const [software, setSoftware] = useState("");
  const [commercial, setCommercial] = useState("");
  const [newSoftware, setNewSoftware] = useState("");
  const [newCommercial, setNewCommercial] = useState("");
  const [saving, setSaving] = useState(false);

  const softwareItems = useMemo(() => parseCatalogText(software), [software]);
  const commercialItems = useMemo(() => parseCatalogText(commercial), [commercial]);

  const loadCatalogs = async () => {
    try {
      const { data } = await api.get("/clients/catalogs");
      setSoftware((data?.software || []).join("\n"));
      setCommercial((data?.commercial || []).join("\n"));
    } catch (error) {
      toastError(error);
    }
  };

  useEffect(() => {
    loadCatalogs();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      const { data } = await api.put("/clients/catalogs", {
        software,
        commercial
      });
      setSoftware((data?.software || []).join("\n"));
      setCommercial((data?.commercial || []).join("\n"));
      const blockedSoftware = Array.isArray(data?.blocked?.software) ? data.blocked.software : [];
      const blockedCommercial = Array.isArray(data?.blocked?.commercial) ? data.blocked.commercial : [];

      if (blockedSoftware.length || blockedCommercial.length) {
        toast.warning("Algunas clasificaciones no se eliminaron porque ya estan relacionadas con clientes.");
      }
      toast.success("Clasificaciones actualizadas.");
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const appendCatalogItem = type => {
    const value = normalizeCatalogValue(type === "software" ? newSoftware : newCommercial);

    if (!value) {
      toast.warning("Escriba una clasificacion antes de agregarla.");
      return;
    }

    if (type === "software") {
      const nextItems = Array.from(new Set([...softwareItems, value]));
      setSoftware(nextItems.join("\n"));
      setNewSoftware("");
      return;
    }

    const nextItems = Array.from(new Set([...commercialItems, value]));
    setCommercial(nextItems.join("\n"));
    setNewCommercial("");
  };

  const removeCatalogItem = (type, value) => {
    if (type === "software") {
      setSoftware(softwareItems.filter(item => item !== value).join("\n"));
      return;
    }

    setCommercial(commercialItems.filter(item => item !== value).join("\n"));
  };

  if (!isAdmin) {
    return (
      <MainContainer>
        <MainHeader>
          <Title>Clasificaciones de clientes</Title>
        </MainHeader>
        <Paper className={classes.pageCard}>
          <Typography>Acceso restringido a administradores.</Typography>
        </Paper>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>Clasificaciones de clientes</Title>
        <MainHeaderButtonsWrapper>
          <Button variant="outlined" color="primary" onClick={loadCatalogs} disabled={saving}>
            Recargar
          </Button>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.pageCard}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box className={classes.infoBox}>
              <Typography variant="subtitle1" style={{ fontWeight: 800 }}>
                Catalogos manuales
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Puede agregar, quitar y guardar clasificaciones desde esta pantalla. Estos catalogos
                alimentan el formulario operativo de clientes.
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper elevation={0} className={classes.managerCard}>
              <Typography variant="subtitle1" style={{ fontWeight: 800, marginBottom: 12 }}>
                Clasificacion de software
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                value={newSoftware}
                onChange={e => setNewSoftware(e.target.value)}
                placeholder="Ejemplo: FACILWEB NUBE"
                onKeyPress={e => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    appendCatalogItem("software");
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => appendCatalogItem("software")} edge="end">
                        <AddIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <Box mt={2} className={classes.chipWrap}>
                {softwareItems.length ? softwareItems.map(item => (
                  <Chip
                    key={item}
                    label={item}
                    color="primary"
                    onDelete={() => removeCatalogItem("software", item)}
                    deleteIcon={<DeleteOutlineIcon />}
                  />
                )) : (
                  <Box className={classes.emptyState}>
                    <Typography variant="body2" color="textSecondary">
                      Aun no hay clasificaciones de software cargadas.
                    </Typography>
                  </Box>
                )}
              </Box>
              <Box mt={2}>
                <TextField
                  fullWidth
                  multiline
                  minRows={6}
                  variant="outlined"
                  label="Vista tecnica"
                  value={software}
                  onChange={e => setSoftware(e.target.value)}
                  placeholder={"FACILWEB NUBE\nFACILWEB ESCRITORIO\nFACILWEB FE\nSOLUWHATS"}
                />
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper elevation={0} className={classes.managerCard}>
              <Typography variant="subtitle1" style={{ fontWeight: 800, marginBottom: 12 }}>
                Clasificacion comercial
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                value={newCommercial}
                onChange={e => setNewCommercial(e.target.value)}
                placeholder="Ejemplo: MENSUAL"
                onKeyPress={e => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    appendCatalogItem("commercial");
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => appendCatalogItem("commercial")} edge="end">
                        <AddIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <Box mt={2} className={classes.chipWrap}>
                {commercialItems.length ? commercialItems.map(item => (
                  <Chip
                    key={item}
                    label={item}
                    style={{
                      backgroundColor: "rgba(29,154,108,0.16)",
                      color: "#1D9A6C",
                      fontWeight: 700
                    }}
                    onDelete={() => removeCatalogItem("commercial", item)}
                    deleteIcon={<DeleteOutlineIcon />}
                  />
                )) : (
                  <Box className={classes.emptyState}>
                    <Typography variant="body2" color="textSecondary">
                      Aun no hay clasificaciones comerciales cargadas.
                    </Typography>
                  </Box>
                )}
              </Box>
              <Box mt={2}>
                <TextField
                  fullWidth
                  multiline
                  minRows={6}
                  variant="outlined"
                  label="Vista tecnica"
                  value={commercial}
                  onChange={e => setCommercial(e.target.value)}
                  placeholder={"MENSUAL\nANUAL\nPROSPECTO\nCLIENTE ACTIVO"}
                />
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Box className={classes.footerActions}>
              <Button variant="outlined" color="primary" onClick={loadCatalogs} disabled={saving}>
                Recargar
              </Button>
              <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
                {saving ? "Guardando..." : "Guardar clasificaciones"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </MainContainer>
  );
};

export default ClientCatalogs;
